/**
 * Placeholder meshes for deployables (barrier, turret, drone, field, marker), created on spawn
 * events and synced from the state each frame (drones move, barriers wear down, fields pulse).
 */
import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  RingGeometry,
  SphereGeometry,
  type Object3D,
  type Scene,
} from 'three/webgpu';
import type { Deployable } from '@steelwake/shared/combat';

import { createVfxMaterial } from './vfx/material.js';

interface View {
  kind: Deployable['kind'];
  root: Object3D;
  solid: MeshStandardMaterial | null;
}

/** Shared geometries and materials so every deployable costs at most two draw calls. */
export class DeployableViews {
  private readonly views = new Map<number, View>();
  private readonly group = new Group();
  private readonly barrierGeometry = new BoxGeometry(1, 1, 1);
  private readonly turretBase = new CylinderGeometry(0.9, 1.1, 1.2, 12);
  private readonly turretHead = new BoxGeometry(0.9, 0.6, 1.6);
  private readonly droneGeometry = new SphereGeometry(0.5, 12, 10);
  private readonly fieldGeometry = new RingGeometry(0.85, 1, 48);
  private readonly markerGeometry = new CylinderGeometry(0.35, 0.35, 30, 10, 1, true);
  private readonly fieldMaterial = createVfxMaterial(0x5cf2ff, 1.5).material;
  private readonly markerMaterial = createVfxMaterial(0xff4040, 2).material;

  constructor(
    scene: Scene,
    private readonly accents: [string, string],
  ) {
    scene.add(this.group);
  }

  private solidMaterial(slot: 0 | 1, opacity: number): MeshStandardMaterial {
    const m = new MeshStandardMaterial({
      color: this.accents[slot],
      emissive: this.accents[slot],
      emissiveIntensity: 0.35,
      roughness: 0.5,
      metalness: 0.4,
    });
    if (opacity < 1) {
      m.transparent = true;
      m.opacity = opacity;
    }
    return m;
  }

  private create(d: Deployable): View {
    const slot: 0 | 1 = d.slot;
    switch (d.kind) {
      case 'barrier': {
        const he = d.params.halfExtents ?? { x: 3, y: 1.5, z: 0.3 };
        const mat = this.solidMaterial(slot, 0.55);
        const mesh = new Mesh(this.barrierGeometry, mat);
        mesh.scale.set(he.x * 2, he.y * 2, he.z * 2);
        mesh.position.set(d.position.x, d.position.y + he.y, d.position.z);
        mesh.rotation.y = d.yaw;
        mesh.castShadow = true;
        return { kind: d.kind, root: mesh, solid: mat };
      }
      case 'turret': {
        const mat = this.solidMaterial(slot, 1);
        const root = new Group();
        const base = new Mesh(this.turretBase, mat);
        base.position.y = 0.6;
        const head = new Mesh(this.turretHead, mat);
        head.position.y = 1.5;
        base.castShadow = true;
        head.castShadow = true;
        root.add(base, head);
        root.position.set(d.position.x, d.position.y, d.position.z);
        root.rotation.y = d.yaw;
        return { kind: d.kind, root, solid: mat };
      }
      case 'drone': {
        const mat = this.solidMaterial(slot, 1);
        const mesh = new Mesh(this.droneGeometry, mat);
        mesh.position.set(d.position.x, d.position.y, d.position.z);
        return { kind: d.kind, root: mesh, solid: mat };
      }
      case 'field': {
        const mesh = new Mesh(this.fieldGeometry, this.fieldMaterial);
        const r = d.params.pulse?.radius ?? 9;
        mesh.rotation.x = -Math.PI / 2;
        mesh.scale.set(r, r, 1);
        mesh.position.set(d.position.x, d.position.y + 0.15, d.position.z);
        return { kind: d.kind, root: mesh, solid: null };
      }
      case 'marker': {
        const mesh = new Mesh(this.markerGeometry, this.markerMaterial);
        mesh.position.set(d.position.x, d.position.y + 15, d.position.z);
        return { kind: d.kind, root: mesh, solid: null };
      }
    }
  }

  /** Reconciles the views with the state's deployable list. */
  sync(deployables: readonly Deployable[], time: number): void {
    const seen = new Set<number>();
    for (const d of deployables) {
      seen.add(d.id);
      let v = this.views.get(d.id);
      if (!v) {
        v = this.create(d);
        this.views.set(d.id, v);
        this.group.add(v.root);
      }
      if (d.kind === 'drone') v.root.position.set(d.position.x, d.position.y, d.position.z);
      if (d.kind === 'barrier' && v.solid)
        v.solid.opacity = 0.25 + 0.4 * (d.maxHp > 0 ? d.hp / d.maxHp : 1);
      if (d.kind === 'field') {
        const r = (d.params.pulse?.radius ?? 9) * (0.92 + 0.08 * Math.sin(time * 6));
        v.root.scale.set(r, r, 1);
      }
      if (d.kind === 'turret') v.root.rotation.y = d.yaw;
    }
    for (const [id, v] of this.views) {
      if (seen.has(id)) continue;
      this.group.remove(v.root);
      v.solid?.dispose();
      this.views.delete(id);
    }
  }

  count(): number {
    return this.views.size;
  }

  dispose(): void {
    for (const v of this.views.values()) {
      this.group.remove(v.root);
      v.solid?.dispose();
    }
    this.views.clear();
    this.group.removeFromParent();
    for (const g of [
      this.barrierGeometry,
      this.turretBase,
      this.turretHead,
      this.droneGeometry,
      this.fieldGeometry,
      this.markerGeometry,
    ]) {
      g.dispose();
    }
    this.fieldMaterial.dispose();
    this.markerMaterial.dispose();
  }
}
